import React, { useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import UpdateIcon from '@mui/icons-material/Update';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { descriptionMarkdownComponents } from '../EntityDetailDrawer';
import SeverityChip from './SeverityChip';
import { Alternatives, ConfidencePill, PolicyRefs } from './DecisionDetails';
import { ExplainabilityTrace, FullDecision, isDecisionReference, optionSummary, techniqueOptions } from './explainabilityTypes';
import { dsVar, ink } from '../../theme/tokens';

/**
 * A stage's observations, each followed by the decisions it led to and the
 * technique each decision chose, stacked so they read top to bottom in a drawer.
 *
 * Styled with sx, not Tailwind classes: this renders inside a portalled drawer.
 */

// Rationale is model-written markdown, often with `column_names` inline.
const rationaleMarkdown = {
    ...descriptionMarkdownComponents,
    code: ({ children }: { children?: React.ReactNode }) => (
        <Box component="code" sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.75rem', color: dsVar('fg-body'), backgroundColor: dsVar('raised'), borderRadius: '4px', px: 0.5 }}>
            {children}
        </Box>
    ),
};

/** Rationale longer than this starts clamped. */
const LONG_RATIONALE = 320;

const label = (colour: string) => ({
    fontSize: '0.625rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' as const, color: ink(colour),
    display: 'flex', alignItems: 'center', gap: 0.75,
});

const Dot: React.FC<{ colour: string }> = ({ colour }) => (
    <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: colour }} />
);

/** Marks a trace item a later run has replaced; the API flags only those. */
const SupersededTag = () => (
    <Box
        component="span"
        sx={{
            fontSize: '0.5625rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: ink('#FCD34D'),
            backgroundColor: 'rgba(251, 191, 36, 0.1)', border: '1px solid rgba(251, 191, 36, 0.3)', borderRadius: '4px', px: 0.75, py: 0.125,
        }}
    >
        Superseded
    </Box>
);

/** Marks the decision that set the node's result. */
const SetResultTag = () => (
    <Box
        component="span"
        sx={{
            fontSize: '0.5625rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: ink('#34D399'),
            backgroundColor: 'rgba(52, 211, 153, 0.1)', border: '1px solid rgba(52, 211, 153, 0.3)', borderRadius: '4px', px: 0.75, py: 0.125,
        }}
    >
        Set the result
    </Box>
);

export const Rationale: React.FC<{ text: string }> = ({ text }) => {
    const long = text.length > LONG_RATIONALE;
    const [expanded, setExpanded] = useState(false);
    const clamped = long && !expanded;
    return (
        <Box>
            <Box
                sx={{
                    '& p': { fontSize: '0.8125rem' },
                    ...(clamped && {
                        maxHeight: '6.5em', overflow: 'hidden',
                        maskImage: 'linear-gradient(to bottom, black 55%, transparent)',
                        WebkitMaskImage: 'linear-gradient(to bottom, black 55%, transparent)',
                    }),
                }}
            >
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={rationaleMarkdown}>{text}</ReactMarkdown>
            </Box>
            {long && (
                <Button
                    size="small"
                    onClick={() => setExpanded((v) => !v)}
                    sx={{ color: dsVar('fg-2'), textTransform: 'none', fontSize: '0.75rem', px: 0, minWidth: 0, mt: 0.5 }}
                >
                    {expanded ? 'Show less' : 'Show more'}
                </Button>
            )}
        </Box>
    );
};

interface TechniqueControlProps {
    decision: FullDecision;
    value: string;
    onChange: (techniqueId: string) => void;
    isLastStage: boolean;
}

const TechniqueControl: React.FC<TechniqueControlProps> = ({ decision, value, onChange, isLastStage }) => {
    const options = techniqueOptions(decision);
    if (options.length === 0) return null;
    return (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1.25 }}>
            <Box
                component="select"
                value={value}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => onChange(e.target.value)}
                aria-label={`Technique for ${decision.decision_type}`}
                sx={{
                    flex: 1, minWidth: 0, backgroundColor: dsVar('page'), color: dsVar('fg-body'), border: `1px solid ${dsVar('line')}`,
                    borderRadius: '8px', p: '7px 10px', fontSize: '0.75rem', outline: 'none', cursor: 'pointer',
                }}
            >
                {options.map((tech) => (
                    <option key={tech.technique_id} value={tech.technique_id}>{tech.name}</option>
                ))}
            </Box>
            <Button
                size="small"
                variant="contained"
                disableElevation
                startIcon={isLastStage ? <PlayArrowIcon sx={{ fontSize: 14 }} /> : <UpdateIcon sx={{ fontSize: 14 }} />}
                sx={{
                    flexShrink: 0, fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.04em', borderRadius: '8px',
                    ...(isLastStage
                        ? { backgroundColor: '#10B981', color: '#fff', '&:hover': { backgroundColor: '#059669' } }
                        : { backgroundColor: dsVar('accent'), color: dsVar('on-accent'), '&:hover': { backgroundColor: dsVar('accent') } }),
                }}
            >
                {isLastStage ? 'Proceed' : 'Update'}
            </Button>
        </Box>
    );
};

interface StageTracesProps {
    traces: ExplainabilityTrace[];
    /** The flow's final stage offers Proceed rather than Update. */
    isLastStage: boolean;
    /** Technique picked per decision_id, where it differs from the selected one. */
    techniqueChoices: Record<string, string>;
    onChooseTechnique: (decisionId: string, techniqueId: string) => void;
}

const StageTraces: React.FC<StageTracesProps> = ({ traces, isLastStage, techniqueChoices, onChooseTechnique }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {traces.map((trace, idx) => (
            <Box key={idx} sx={{ border: `1px solid ${dsVar('line')}`, borderRadius: '10px', backgroundColor: dsVar('page'), p: 1.75 }}>
                {trace.observation ? (
                    <>
                        <Typography component="div" sx={label('#60A5FA')}><Dot colour="#60A5FA" />Observation</Typography>
                        <Typography component="div" sx={{ fontSize: '0.875rem', fontWeight: 600, color: dsVar('fg'), mt: 0.75, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            {trace.observation.observation_type}
                            {trace.observation.severity && <SeverityChip severity={trace.observation.severity} />}
                            {trace.observation.is_current === false && <SupersededTag />}
                        </Typography>
                        {trace.observation.description && (
                            <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-2'), fontStyle: 'italic', lineHeight: 1.6, mt: 0.5 }}>
                                {trace.observation.description}
                            </Typography>
                        )}
                    </>
                ) : (
                    // The API groups decisions that cite no observation into one trace, placed last.
                    <Typography component="div" sx={label('#94A3B8')}><Dot colour="#94A3B8" />Decisions without an observation</Typography>
                )}

                {trace.decisions.length > 0 && (
                    <Box sx={{ mt: trace.observation ? 1.5 : 1, pl: 1.5, borderLeft: '2px solid rgba(52, 211, 153, 0.35)', display: 'flex', flexDirection: 'column', gap: 1.75 }}>
                        {trace.decisions.map((decision, dIdx) => (
                            <Box key={decision.decision_id ?? dIdx}>
                                <Typography component="div" sx={label('#34D399')}>
                                    <Dot colour="#34D399" />Decision
                                </Typography>
                                {isDecisionReference(decision) ? (
                                    <>
                                        <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: dsVar('fg-soft'), mt: 0.5 }}>
                                            {decision.decision_type}
                                            {decision.subject && <Box component="span" sx={{ fontWeight: 400, color: dsVar('fg-muted') }}> · {decision.subject}</Box>}
                                        </Typography>
                                        <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-muted'), fontStyle: 'italic', mt: 0.25 }}>
                                            Same decision as above{decision.chosen_option ? ` — chose ${decision.chosen_option}` : ''}
                                        </Typography>
                                    </>
                                ) : (
                                    <>
                                        <Typography component="div" sx={{ fontSize: '0.875rem', fontWeight: 600, color: dsVar('fg'), mt: 0.5, mb: 0.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                            {decision.decision_type}
                                            {decision.subject && <Box component="span" sx={{ fontWeight: 400, color: dsVar('fg-2') }}>· {decision.subject}</Box>}
                                            {decision.outcome_determining && <SetResultTag />}
                                            <ConfidencePill score={decision.confidence_score} />
                                            {decision.is_current === false && <SupersededTag />}
                                        </Typography>
                                        {decision.rationale_text && <Rationale text={decision.rationale_text} />}
                                        {decision.chosen_option && techniqueOptions(decision).length === 0 && (
                                            <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-2'), mt: 0.5 }}>
                                                Chose <Box component="span" sx={{ color: dsVar('fg'), fontWeight: 600 }}>{decision.chosen_option}</Box>
                                            </Typography>
                                        )}
                                        <PolicyRefs codes={decision.policy_refs} />
                                        <Alternatives decision={decision} />
                                        <TechniqueControl
                                            decision={decision}
                                            value={techniqueChoices[decision.decision_id] ?? decision.selected_technique?.technique_id ?? ''}
                                            onChange={(techniqueId) => onChooseTechnique(decision.decision_id, techniqueId)}
                                            isLastStage={isLastStage}
                                        />
                                    </>
                                )}
                            </Box>
                        ))}
                    </Box>
                )}

                {trace.decision_rollups?.map((rollup) => (
                    <Typography key={rollup.decision_type} sx={{ fontSize: '0.75rem', color: dsVar('fg-muted'), mt: 1, pl: 1.5 }}>
                        +{rollup.count - (rollup.shown ?? 0)} more {rollup.decision_type}
                        {optionSummary(rollup.by_option) && `: ${optionSummary(rollup.by_option)}`}
                    </Typography>
                ))}
            </Box>
        ))}
    </Box>
);

export default StageTraces;
